// ===========================================
// Import API
// ===========================================
// API client for Simply Portal CSV import

import { apiClient } from '../services/api';

const BASE_URL = '/admin/import';

// ===========================================
// TYPES
// ===========================================

export interface EnrollmentPreview {
  studentName: string;
  className: string;
  status: 'matched' | 'ambiguous' | 'no_match';
  candidates?: string[];
}

export interface ImportPreviewResponse {
  success: boolean;
  data: {
    counts: {
      classes: number;
      students: number;
      families: number;
      teachers: number;
      locations: number;
      rooms: number;
      instruments: number;
      programLevels: number;
      enrollments: { matched: number; ambiguous: number; noMatch: number };
    };
    warnings: string[];
    parsedClasses: Array<{
      classId: string;
      className: string;
      dayOfWeek: number;
      startTime: string;
      endTime: string;
      durationMins: number;
      maxStudents: number;
      teacherName: string;
      roomName: string;
      locationName: string;
      lessonType: 'INDIVIDUAL' | 'GROUP';
    }>;
    parsedStudents: Array<{
      firstName: string;
      lastName: string;
      birthDate: string | null;
      instrument: string | null;
      programLevel: string | null;
      classDays: number[];
      familyName: string;
      email: string;
      phone: string;
      birthDateWarning?: string;
    }>;
    enrollmentPreview: EnrollmentPreview[];
    lookups: {
      teachers: string[];
      locations: string[];
      rooms: string[];
      instruments: string[];
      programLevels: string[];
      families: string[];
    };
  };
}

export interface ImportEntityResult {
  created: number;
  existing: number;
}

export interface ImportExecuteResponse {
  success: boolean;
  message: string;
  data: {
    success: boolean;
    locations: ImportEntityResult;
    rooms: ImportEntityResult;
    instruments: ImportEntityResult;
    programLevels: ImportEntityResult;
    lessonDurations: ImportEntityResult;
    lessonTypes: ImportEntityResult;
    teachers: ImportEntityResult;
    families: ImportEntityResult;
    parents: ImportEntityResult;
    students: ImportEntityResult;
    lessons: ImportEntityResult;
    enrollments: ImportEntityResult & { ambiguous: number };
    errors: string[];
  };
}

// ===========================================
// API FUNCTIONS
// ===========================================

/**
 * Preview import from CSV files without writing to database
 * @param classesFile - Classes CSV file
 * @param studentsFile - Student Details CSV file
 * @param enrollmentsFile - Optional Enrollments CSV file for direct student-class matching
 */
export async function previewImport(
  classesFile: File,
  studentsFile: File,
  enrollmentsFile?: File
): Promise<ImportPreviewResponse> {
  const formData = new FormData();
  formData.append('classes', classesFile);
  formData.append('students', studentsFile);
  if (enrollmentsFile) {
    formData.append('enrollments', enrollmentsFile);
  }

  return apiClient.post<ImportPreviewResponse>(
    `${BASE_URL}/preview`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
}

/**
 * Execute import from CSV files
 * @param classesFile - Classes CSV file
 * @param studentsFile - Student Details CSV file
 * @param termId - Term ID to assign lessons to
 * @param enrollmentsFile - Optional Enrollments CSV file for direct student-class matching
 */
export async function executeImport(
  classesFile: File,
  studentsFile: File,
  termId: string,
  enrollmentsFile?: File
): Promise<ImportExecuteResponse> {
  const formData = new FormData();
  formData.append('classes', classesFile);
  formData.append('students', studentsFile);
  formData.append('termId', termId);
  if (enrollmentsFile) {
    formData.append('enrollments', enrollmentsFile);
  }

  return apiClient.post<ImportExecuteResponse>(
    `${BASE_URL}/execute`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
}
